/**
 * Registro SNIES de programas y sus resoluciones
 * (`/api/programas-snies`).
 *
 * La regla de alerta D1 vigila los vencimientos: crítico a ≤10 meses (el corte
 * legal para radicar la renovación), alto a <12 y medio entre 12 y 24.
 *
 * Capa de servicios: una función por operación del API. No lanza nunca —cada
 * función devuelve un valor neutro ante el fallo—, así que el llamador debe
 * comprobar el resultado, no envolver en try/catch.
 */

import { http, primerError } from '@/services/http'

/**
 * Registro de los programas ante el Ministerio (SNIES).
 *
 * Es OTRA cosa que el nodo del organigrama: el ministerio registra por
 * programa × municipio × modalidad, así que un programa del árbol puede tener
 * varios registros, cada uno con su código, su resolución y su vencimiento.
 *
 * Nada de lo derivado se guarda ni se recalcula aquí: el vencimiento, la
 * bandera del semáforo y los estados vienen resueltos del backend.
 */

export type Bandera = 'roja' | 'amarilla' | 'verde'

/** Forma con la que el API de programa snies devuelve este dato. */
export interface ResolucionPrograma {
  id_resolucion: number
  id_programa_snies: number
  tipo_acto: string
  numero: string | null
  fecha_emision: string | null
  fecha_ejecutoria: string | null
  vigencia_anios: number | null
  observaciones: string | null
  documento: string | null
  url_descarga: string | null
  /** Derivados: ejecutoria + vigencia. */
  fecha_vencimiento: string | null
  meses_para_vencer: number | null
  bandera: Bandera | null
}

/** Forma con la que el API de programa snies devuelve este dato. */
export interface ProgramaSnies {
  id_programa_snies: number
  codigo_snies: string
  codigo_anterior_icfes: string | null
  id_programa: number
  id_campus: number | null
  titulo_otorgado: string | null
  estado: string | null
  reconocimiento: string | null
  nivel_academico: string | null
  nivel_formacion: string | null
  modalidad: string | null
  area_conocimiento: string | null
  nucleo_basico: string | null
  creditos: number | null
  periodos_duracion: number | null
  periodicidad: string | null
  departamento: string | null
  municipio: string | null
  costo_matricula: number | null
  fecha_registro_snies: string | null
  anios_funcionamiento: number | null
  programa?: { id_programa: number; nombre: string } | null
  campus?: { id_campus: number; nombre: string } | null
  resoluciones?: ResolucionPrograma[]
  // Derivados que añade el backend al listar y al ver el detalle.
  bandera?: Bandera | null
  registro_vigente?: boolean
  alta_calidad_vigente?: boolean
  en_primer_registro?: boolean
  vence_registro?: string | null
  vence_alta_calidad?: string | null
}

/** Forma con la que el API de programa snies devuelve este dato. */
export interface TableroSnies {
  kpis: {
    total_programas: number
    pregrado: number
    posgrado: number
    con_registro_vigente: number
    con_alta_calidad: number
    pct_alta_calidad: number
    en_primer_registro: number
  }
  por_campus: Record<string, number>
  /** Campus → nivel de formación → cuántos programas. */
  matriz_campus_nivel: Record<string, Record<string, number>>
  por_modalidad: Record<string, number>
  banderas: { roja: number; amarilla: number; verde: number; sin_dato: number }
  alertas: Array<{
    id_programa_snies: number
    codigo_snies: string
    programa: string | null
    campus: string | null
    modalidad: string | null
    bandera: Bandera
    vence: string | null
    meses_para_vencer: number | null
  }>
}

/** Forma con la que el API de programa snies devuelve este dato. */
export interface FiltrosSnies {
  id_programa?: number
  id_campus?: number
  nivel_formacion?: string
  nivel_academico?: string
  modalidad?: string
  estado?: string
  bandera?: Bandera
}

const BASE = '/api/programas-snies'

/**
 * Lista programa snies · petición `GET`.
 *
 * @returns ProgramaSnies[]
 */
export async function listarProgramasSniesService(filtros: FiltrosSnies = {}): Promise<ProgramaSnies[]> {
  const query = new URLSearchParams(
    Object.entries(filtros).filter(([, v]) => v !== undefined && v !== null && v !== '') as [string, string][]
  ).toString()
  const r = await http.get<ProgramaSnies[]>(query ? `${BASE}?${query}` : BASE)
  return r.ok ? (r.data ?? []) : []
}

/**
 * Consulta programa snies · petición `GET`.
 *
 * @returns ProgramaSnies | null
 */
export async function getProgramaSniesService(id: number): Promise<ProgramaSnies | null> {
  const r = await http.get<ProgramaSnies>(`${BASE}/${id}`)
  return r.ok ? (r.data ?? null) : null
}

/**
 * Consulta programa snies · petición `GET`.
 *
 * @returns TableroSnies | null
 */
export async function getTableroSniesService(): Promise<TableroSnies | null> {
  const r = await http.get<TableroSnies>(`${BASE}/tablero`)
  return r.ok ? (r.data ?? null) : null
}

/**
 * Consulta programa snies · petición `GET`.
 *
 * @returns { niveles_formacion: string[] modalidades: string[] tipos_acto: string[] }
 */
export async function getCatalogosSniesService(): Promise<{
  niveles_formacion: string[]
  modalidades: string[]
  tipos_acto: string[]
}> {
  const r = await http.get<any>(`${BASE}/catalogos`)
  return r.ok ? (r.data ?? { niveles_formacion: [], modalidades: [], tipos_acto: [] })
    : { niveles_formacion: [], modalidades: [], tipos_acto: [] }
}

/**
 * Crea programa snies · petición `POST`.
 *
 * @returns { ok: boolean; data?: ProgramaSnies; error?: string }
 */
export async function crearProgramaSniesService(
  payload: Partial<ProgramaSnies>
): Promise<{ ok: boolean; data?: ProgramaSnies; error?: string }> {
  const r = await http.post<ProgramaSnies>(BASE, payload)
  return r.ok ? { ok: true, data: r.data ?? undefined } : { ok: false, error: primerError(r, 'No se pudo crear el registro') }
}

/**
 * Actualiza programa snies · petición `PUT`.
 *
 * @returns { ok: boolean; error?: string }
 */
export async function actualizarProgramaSniesService(
  id: number,
  payload: Partial<ProgramaSnies>
): Promise<{ ok: boolean; error?: string }> {
  const r = await http.put(`${BASE}/${id}`, payload)
  return r.ok ? { ok: true } : { ok: false, error: primerError(r, 'No se pudo actualizar el registro') }
}

/** Falla con 422 si hay autoevaluaciones hechas sobre el registro. */
export async function eliminarProgramaSniesService(id: number): Promise<{ ok: boolean; error?: string }> {
  const r = await http.delete(`${BASE}/${id}`)
  return r.ok ? { ok: true } : { ok: false, error: primerError(r, 'No se pudo eliminar el registro') }
}

/**
 * Crea programa snies · petición `POST`.
 *
 * @returns { ok: boolean; error?: string }
 */
export async function agregarResolucionService(
  idProgramaSnies: number,
  payload: Partial<ResolucionPrograma>
): Promise<{ ok: boolean; error?: string }> {
  const r = await http.post(`${BASE}/${idProgramaSnies}/resoluciones`, payload)
  return r.ok ? { ok: true } : { ok: false, error: primerError(r, 'No se pudo agregar la resolución') }
}

/**
 * Actualiza programa snies · `PUT /api/resoluciones-programa`.
 *
 * @returns { ok: boolean; error?: string }
 */
export async function actualizarResolucionService(
  id: number,
  payload: Partial<ResolucionPrograma>
): Promise<{ ok: boolean; error?: string }> {
  const r = await http.put(`/api/resoluciones-programa/${id}`, payload)
  return r.ok ? { ok: true } : { ok: false, error: primerError(r, 'No se pudo actualizar la resolución') }
}

/**
 * Elimina programa snies · `DELETE /api/resoluciones-programa`.
 *
 * @returns boolean
 */
export async function eliminarResolucionService(id: number): Promise<boolean> {
  const r = await http.delete(`/api/resoluciones-programa/${id}`)
  return r.ok
}

/** Etiqueta legible del tipo de acto administrativo. */
export function etiquetaTipoActo(tipo: string): string {
  const etiquetas: Record<string, string> = {
    primer_registro: 'Primer registro',
    renovacion_rc: 'Renovación de Registro Calificado',
    modificacion: 'Modificación',
    ampliacion_lugar: 'Ampliación de lugar de desarrollo',
    acreditacion: 'Acreditación de alta calidad',
    reacreditacion: 'Reacreditación'
  }
  return etiquetas[tipo] ?? tipo
}
